import { useCallback } from 'react'

import { useMutation, useQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'

export const GetPushNotificationPreferencesQuery = graphql(`
  query getPushNotificationPreferencesQuery {
    pushNotificationPreferences {
      __typename
      ... on PushNotificationPreferencesResponse {
        preferences {
          id
          name
          description
          subscribed
          cioTopicId
        }
      }
      ... on PushNotificationPreferenceError {
        message
      }
    }
  }
`)

export const UpdatePushNotificationPreferencesMutation = graphql(`
  mutation pushNotificationPreferences(
    $input: [PushNotificationPreferenceInput!]!
  ) {
    pushNotificationPreferences(input: $input) {
      __typename
      ... on PushNotificationPreferencesResponse {
        preferences {
          id
          name
          description
          subscribed
          cioTopicId
        }
      }
      ... on PushNotificationPreferenceError {
        message
      }
    }
  }
`)

type NotificationOptionProps = {
  id: string
  subscribed: boolean
}

type PushNotificationPreference = {
  id: string
  name: string
  description: string
  subscribed: boolean
  cioTopicId?: string | null
}

export const usePushNotificationPreferences = ({
  skip = false,
}: { skip?: boolean } = {}): {
  preferencesList: PushNotificationPreference[] | null
  updateNotificationSetting: (
    options: NotificationOptionProps,
  ) => Promise<boolean>
  loading: boolean
  error: Error | undefined
} => {
  const { data, loading, error } = useQuery(
    GetPushNotificationPreferencesQuery,
    { skip },
  )
  const [updatePushNotificationPreferences] = useMutation(
    UpdatePushNotificationPreferencesMutation,
  )

  const updateNotificationSetting = useCallback(
    async (options: NotificationOptionProps): Promise<boolean> => {
      try {
        await updatePushNotificationPreferences({
          variables: {
            input: [
              {
                id: options.id,
                subscribed: options.subscribed,
              },
            ],
          },
        })
        return true
      } catch {
        toast.error(
          'Sorry! There was an issue updating your push notification preferences. Please try again.',
        )
        return false
      }
    },
    [updatePushNotificationPreferences],
  )

  const preferencesList =
    data?.pushNotificationPreferences.__typename ===
    'PushNotificationPreferencesResponse'
      ? data?.pushNotificationPreferences.preferences
      : null

  return {
    preferencesList,
    updateNotificationSetting,
    loading,
    error,
  }
}
